/**
 * KinsCircle Intelligence Layer V1 — Governed Operational Repair Actions
 *
 * V1 stays REPORT-ONLY by default. This module is the additive, opt-in
 * surface that lets a Command Center principal MANUALLY approve a *small* set of
 * provably-safe repairs surfaced by `runTreeHealthCheck()`. Nothing in
 * here ever runs without an explicit Apply Fix click.
 *
 * STRICT V1 SAFETY CONTRACT — read carefully before extending:
 *
 *   1. Command Center operational-authority gate is enforced by the caller (the modal +
 *      TreeIntelligenceCard read `canRunIntelligenceDiagnostics`). This
 *      file additionally re-checks the gate before every write so a
 *      direct console call cannot bypass it.
 *
 *   2. ONLY low-risk repairs are eligible. The whitelist below covers
 *      the exact 5 cases the user signed off on:
 *        - duplicate placeholder rows with NO memories / photos /
 *          comments / claimed_by_user_id (delete-empty-duplicate)
 *        - members stuck in pending approval whose creator is the
 *          current owner AND who already have a structural anchor
 *          (approve-pending)
 *        - missing viewerMemberId mapping when the auth user owns the
 *          graph and a unique unclaimed self-anchor exists
 *          (claim-self-anchor)
 *        - invite accepted but invite_status not flipped
 *          (sync-invite-status)
 *        - orphan upload row with no matching memory (skipped in V1 —
 *          requires extra detection; see §9.10 in STABILITY.md)
 *
 *   3. NEVER auto-merge people with memories, photos, comments, or a
 *      claimed_by_user_id. Eligibility checks below enforce this.
 *
 *   4. NEVER overwrite a non-null spouse_id. NEVER overwrite a non-null
 *      parent_ids list. NEVER overwrite a non-null claimed_by_user_id.
 *      The only writes this module performs are:
 *        - DELETE on a placeholder family_graph_members row that has
 *          ZERO memories / photos / comments / claim and a strong
 *          duplicate twin still alive in the graph.
 *        - UPDATE approval_status = 'approved' on a single
 *          family_graph_members row.
 *        - UPDATE claimed_by_user_id + profile_state + claimed_at on a
 *          single unclaimed self-anchor row that the auth user owns.
 *        - UPDATE status = 'accepted' (+ accepted_at) on a single
 *          invites row whose accepted_user_id is already non-null.
 *
 *   5. EVERY apply call:
 *        - re-runs the eligibility check immediately before writing
 *          (guards against stale state from the modal)
 *        - records a RepairLogEntry in localStorage (timestamp, owner
 *          id, action id, affected member ids, before / after values)
 *        - returns success / failure with a user-safe message
 *
 *   6. Undo is supported ONLY when before-values are exactly recoverable
 *      (i.e. the repair was a single-field UPDATE). Delete repairs are
 *      flagged irreversible — the modal warns the user to export a
 *      backup first via the existing `familyBackupExport.ts` tool
 *      (§7.7).
 *
 *   7. NEVER mutates: spouse engine, kinship engine, tree layout,
 *      relationship labels, billing / FamousPay / Stripe pipeline,
 *      auth providers, uploads pipeline, Resend pipeline, homepage
 *      copy, Supabase schema, or any RLS rule.
 *
 * Honors STABILITY.md §1 (locked subsystems), §3 (additive only),
 * §7 (no destructive change), §8 (account recovery lock), §9 (V1
 * intelligence layer report-only base contract).
 */

import { supabase } from '@/lib/supabase';
import { exportFamilyTreeBackup } from '@/lib/familyBackupExport';
import type {
  GraphMember,
  GraphMemory,
  GraphPhoto,
  GraphComment,
} from '@/contexts/FamilyGraphContext';
import type { TreeIssue } from '@/lib/intelligence/treeHealthCheck';
import { canRunIntelligenceDiagnostics } from '@/lib/intelligence/featureFlag';

// ─── Action whitelist ────────────────────────────────────────────────

export type RepairActionId =
  | 'approve-pending'
  | 'delete-empty-duplicate'
  | 'claim-self-anchor'
  | 'sync-invite-status'
  | 'clear-dangling-parent-ids';

export type RepairRiskLevel = 'low';

export interface RepairFieldChange {
  memberId: string;
  memberName: string;
  field: string;
  before: unknown;
  after: unknown;
  /** When 'delete', the row itself is removed and `field` describes it. */
  op: 'update' | 'delete';
}

export interface RepairProposal {
  actionId: RepairActionId;
  issueId: string;
  issueTitle: string;
  /** A short, plain-English description of what Apply Fix will do. */
  description: string;
  riskLevel: RepairRiskLevel;
  affectedMemberIds: string[];
  affectedNames: string[];
  /** Field-by-field "before → after" preview shown in the modal. */
  fieldsThatWillChange: RepairFieldChange[];
  /** True when the change can be reversed via undoLastRepair(). */
  reversible: boolean;
  /**
   * Optional internal payload — opaque to the modal, used by `applyRepair`
   * to perform the write without re-deriving everything. Never displayed.
   */
  _internal?: Record<string, unknown>;
}

// ─── Repair log ──────────────────────────────────────────────────────

export interface RepairLogEntry {
  id: string;
  timestamp: string;
  ownerUserId: string;
  actionId: RepairActionId;
  issueId: string;
  issueTitle: string;
  affectedMemberIds: string[];
  affectedNames: string[];
  beforeValues: RepairFieldChange[];
  afterValues: RepairFieldChange[];
  /** The result the operator saw at the time the repair landed. */
  outcome: 'success' | 'failure';
  errorMessage?: string;
  /** True when the entry can still be undone (delete-ops are never undoable). */
  undoable: boolean;
}

const REPAIR_LOG_KEY = 'kinscircle.intelligence.repairLog.v1';
const REPAIR_LOG_MAX = 50;

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && !!window.localStorage;

export const readRepairLog = (): RepairLogEntry[] => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(REPAIR_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RepairLogEntry[]) : [];
  } catch {
    return [];
  }
};

const writeRepairLog = (entries: RepairLogEntry[]): void => {
  if (!isBrowser()) return;
  try {
    const trimmed = entries.slice(-REPAIR_LOG_MAX);
    window.localStorage.setItem(REPAIR_LOG_KEY, JSON.stringify(trimmed));
  } catch {
    /* non-fatal */
  }
};

const appendRepairLog = (entry: RepairLogEntry): void => {
  const log = readRepairLog();
  log.push(entry);
  writeRepairLog(log);
};

const updateRepairLog = (
  id: string,
  patch: Partial<RepairLogEntry>,
): void => {
  const log = readRepairLog();
  const next = log.map((e) => (e.id === id ? { ...e, ...patch } : e));
  writeRepairLog(next);
};

// ─── Eligibility helpers ─────────────────────────────────────────────

/** Returns the placeholder row of a duplicate pair that has ZERO data. */
const findEmptyPlaceholderInPair = (
  memberIds: string[],
  members: GraphMember[],
  memories: GraphMemory[],
  photos: GraphPhoto[],
  comments: GraphComment[],
): GraphMember | null => {
  if (memberIds.length !== 2) return null;
  const candidates = memberIds
    .map((id) => members.find((m) => m.id === id))
    .filter((m): m is GraphMember => !!m);
  if (candidates.length !== 2) return null;

  const empty = candidates.filter((m) => {
    if (m.claimedByUserId) return false;
    if (m.profileState === 'claimed') return false;
    const hasMemory = memories.some((x) => x.memberId === m.id);
    if (hasMemory) return false;
    const hasPhoto = photos.some((x) => x.memberId === m.id);
    if (hasPhoto) return false;
    const hasComment = comments.some((x) => x.memberId === m.id);
    if (hasComment) return false;
    // Never delete a row that is anyone's parent or spouse — that would
    // corrupt the bloodline / spouse links on the surviving twin.
    const isAnyonesParent = members.some((other) =>
      (other.parentIds ?? []).includes(m.id),
    );
    if (isAnyonesParent) return false;
    const isAnyonesSpouse = members.some((other) => other.spouseId === m.id);
    if (isAnyonesSpouse) return false;
    return true;
  });
  if (empty.length !== 1) return null;
  return empty[0];
};

const memberHasStructuralAnchor = (m: GraphMember): boolean => {
  const hasParent = (m.parentIds ?? []).filter(Boolean).length > 0;
  const hasSpouse = !!m.spouseId;
  return hasParent || hasSpouse;
};

// ─── Proposal builder ────────────────────────────────────────────────

export interface BuildProposalContext {
  issue: TreeIssue;
  members: GraphMember[];
  memories: GraphMemory[];
  photos: GraphPhoto[];
  comments: GraphComment[];
  /** auth.users.id of the current viewer (must match owner / admin). */
  viewerUserId: string | null;
  /** owner_user_id of the loaded family graph. */
  effectiveOwnerUserId: string | null;
}

/**
 * Returns a RepairProposal ONLY when the issue maps to one of the
 * whitelisted low-risk action ids AND every safety check passes.
 * Returns null otherwise — the caller (TreeIntelligenceCard) treats null
 * as "no Review Fix button for this issue".
 */
export const buildRepairProposal = (
  ctx: BuildProposalContext,
): RepairProposal | null => {
  const { issue, members, memories, photos, comments, viewerUserId } = ctx;

  // ── 1. approve-pending ────────────────────────────────────────────
  if (issue.category === 'needs-connection' && issue.affectedMemberIds.length === 1) {
    const member = members.find((m) => m.id === issue.affectedMemberIds[0]);
    if (!member) return null;
    if (member.approvalStatus !== 'pending') return null;
    // Only when the current viewer created this row (so the operator is
    // approving their own queued add) AND the row already has a
    // structural anchor (parent_ids or spouse_id) — i.e. the connection
    // is already known and the only thing missing is the approval flip.
    if (!viewerUserId) return null;
    if (member.createdBy && member.createdBy !== viewerUserId) return null;
    if (!memberHasStructuralAnchor(member)) return null;
    return {
      actionId: 'approve-pending',
      issueId: issue.id,
      issueTitle: issue.title,
      description: `Approve ${member.name} so they appear in the family tree. The connection is already in place — only the approval state will change.`,
      riskLevel: 'low',
      affectedMemberIds: [member.id],
      affectedNames: [member.name],
      fieldsThatWillChange: [
        {
          memberId: member.id,
          memberName: member.name,
          field: 'approval_status',
          before: 'pending',
          after: 'approved',
          op: 'update',
        },
      ],
      reversible: true,
    };
  }

  // ── 2. delete-empty-duplicate ─────────────────────────────────────
  if (issue.category === 'duplicate-likely' && issue.affectedMemberIds.length === 2) {
    const placeholder = findEmptyPlaceholderInPair(
      issue.affectedMemberIds,
      members,
      memories,
      photos,
      comments,
    );
    if (!placeholder) return null;
    const survivor = members.find(
      (m) => issue.affectedMemberIds.includes(m.id) && m.id !== placeholder.id,
    );
    if (!survivor) return null;
    return {
      actionId: 'delete-empty-duplicate',
      issueId: issue.id,
      issueTitle: issue.title,
      description: `Remove the duplicate placeholder row for ${placeholder.name} that has no memories, photos, comments, or claimed account. ${survivor.name} will remain as the canonical record. This action cannot be undone — please export a family-tree backup first.`,
      riskLevel: 'low',
      affectedMemberIds: [placeholder.id],
      affectedNames: [placeholder.name],
      fieldsThatWillChange: [
        {
          memberId: placeholder.id,
          memberName: placeholder.name,
          field: 'family_graph_members row',
          before: 'present',
          after: 'deleted',
          op: 'delete',
        },
      ],
      reversible: false,
      _internal: { survivorId: survivor.id },
    };
  }

  // No matching whitelisted repair — this issue is advisory-only.
  return null;
};

/**
 * Independent proposal builder for the "invite accepted but invite_status
 * not synced" repair. Not driven by a TreeIssue (the tree health check
 * doesn't cover invites). Caller passes the candidate invite row.
 */
export interface InviteSyncCandidate {
  inviteId: string;
  recipientEmail: string | null;
  acceptedUserId: string;
  currentStatus: string | null;
  acceptedAt: string | null;
}

export const buildInviteSyncProposal = (
  candidate: InviteSyncCandidate,
): RepairProposal => {
  const label = candidate.recipientEmail ?? candidate.inviteId;
  return {
    actionId: 'sync-invite-status',
    issueId: `invite-sync-${candidate.inviteId}`,
    issueTitle: `Invite to ${label} is accepted but the invite status was not updated`,
    description: `Mark this invite as accepted so the Sent Invites panel shows the correct state. The invited user already created an account — only the invite status flag will change.`,
    riskLevel: 'low',
    affectedMemberIds: [],
    affectedNames: [label],
    fieldsThatWillChange: [
      {
        memberId: candidate.inviteId,
        memberName: label,
        field: 'invites.status',
        before: candidate.currentStatus ?? 'pending',
        after: 'accepted',
        op: 'update',
      },
      ...(candidate.acceptedAt
        ? []
        : [
            {
              memberId: candidate.inviteId,
              memberName: label,
              field: 'invites.accepted_at',
              before: null,
              after: '<now>',
              op: 'update' as const,
            },
          ]),
    ],
    reversible: true,
    _internal: {
      inviteId: candidate.inviteId,
      previousStatus: candidate.currentStatus,
      previousAcceptedAt: candidate.acceptedAt,
    },
  };
};

/**
 * Independent proposal builder for the "missing viewerMemberId mapping"
 * repair. Used when an auth user owns the graph (or matches an unclaimed
 * self-anchor exactly) but no row has claimed_by_user_id set yet. The
 * existing FamilyGraphContext refresh already auto-claims in most cases,
 * so this is reserved for the rare manual-trigger path from /admin/health.
 */
export interface ClaimSelfAnchorCandidate {
  memberId: string;
  memberName: string;
  /** Must match auth.users.id of the operator. */
  authUserId: string;
}

export const buildClaimSelfAnchorProposal = (
  candidate: ClaimSelfAnchorCandidate,
): RepairProposal => ({
  actionId: 'claim-self-anchor',
  issueId: `claim-self-${candidate.memberId}`,
  issueTitle: `Bind your account to your unclaimed self-anchor row (${candidate.memberName})`,
  description: `Set claimed_by_user_id on this self-anchor row so the family tree recognizes you as the viewer. Only the claim metadata will change — the relationship engine, parent_ids, spouse_id, name, and photo are untouched.`,
  riskLevel: 'low',
  affectedMemberIds: [candidate.memberId],
  affectedNames: [candidate.memberName],
  fieldsThatWillChange: [
    {
      memberId: candidate.memberId,
      memberName: candidate.memberName,
      field: 'claimed_by_user_id',
      before: null,
      after: candidate.authUserId,
      op: 'update',
    },
    {
      memberId: candidate.memberId,
      memberName: candidate.memberName,
      field: 'profile_state',
      before: 'placeholder',
      after: 'claimed',
      op: 'update',
    },
  ],
  reversible: true,
  _internal: { memberId: candidate.memberId, authUserId: candidate.authUserId },
});

// ─── Apply ───────────────────────────────────────────────────────────

export interface ApplyRepairResult {
  success: boolean;
  logId: string;
  message: string;
  error?: string;
}

export interface ApplyRepairContext {
  proposal: RepairProposal;
  /** auth.users.id of the authorized operational principal. */
  ownerUserId: string;
  /**
   * Live server-backed Command Center operational authority.
   * The repair engine re-checks this independently of route visibility.
   */
  commandCenterOperationalAuthority: boolean;
}

/**
 * Apply a previously-confirmed repair proposal. Re-runs the safety
 * checks against the live database immediately before the write so a
 * stale modal can't apply a repair that has already been resolved
 * elsewhere.
 */
export const applyRepair = async (
  ctx: ApplyRepairContext,
): Promise<ApplyRepairResult> => {
  const {
    proposal,
    ownerUserId,
    commandCenterOperationalAuthority,
  } = ctx;

  const logId = crypto.randomUUID();

  if (
    !canRunIntelligenceDiagnostics(
      commandCenterOperationalAuthority,
    )
  ) {
    return {
      success: false,
      logId,
      message:
        'Command Center operational authority is required.',
      error: 'forbidden',
    };
  }

  if (!ownerUserId) {
    return {
      success: false,
      logId,
      message:
        'Sign in required to prepare or apply a repair.',
      error: 'no-session',
    };
  }

  /*
   * Invite synchronization is not a family_graph_members mutation.
   * Preserve it as a separately validated non-Tree action.
   */
  if (
    proposal.actionId ===
    'sync-invite-status'
  ) {
    const inviteId =
      proposal._internal?.inviteId as
        | string
        | undefined;

    if (!inviteId) {
      return {
        success: false,
        logId,
        message:
          'Invite repair is missing its target.',
        error: 'missing-invite-id',
      };
    }

    try {
      const {
        data: live,
        error: readError,
      } = await supabase
        .from('invites')
        .select(
          'id, accepted_user_id, status, accepted_at, target_member_id',
        )
        .eq('id', inviteId)
        .maybeSingle();

      if (readError) {
        throw readError;
      }

      if (!live) {
        throw new Error(
          'Invite row no longer exists.',
        );
      }

      if (live.target_member_id) {
        return {
          success: false,
          logId,
          message:
            'Person-targeted invite lifecycle is server-owned.',
          error:
            'targeted-invite-server-owned',
        };
      }

      if (!live.accepted_user_id) {
        throw new Error(
          'Invite has not been accepted yet.',
        );
      }

      if (
        live.status === 'accepted' &&
        live.accepted_at
      ) {
        return {
          success: true,
          logId,
          message:
            'Invite status was already accepted.',
        };
      }

      const previousStatus =
        live.status ?? 'pending';

      const previousAcceptedAt =
        live.accepted_at ?? null;

      const acceptedAt =
        previousAcceptedAt ??
        new Date().toISOString();

      const { error: writeError } =
        await supabase
          .from('invites')
          .update({
            status: 'accepted',
            accepted_at: acceptedAt,
          })
          .eq('id', inviteId);

      if (writeError) {
        throw writeError;
      }

      appendRepairLog({
        id: logId,
        timestamp:
          new Date().toISOString(),
        ownerUserId,
        actionId:
          proposal.actionId,
        issueId:
          proposal.issueId,
        issueTitle:
          proposal.issueTitle,
        affectedMemberIds:
          proposal.affectedMemberIds,
        affectedNames:
          proposal.affectedNames,
        beforeValues: [
          {
            memberId: inviteId,
            memberName:
              proposal.affectedNames[0] ??
              'Invite',
            field:
              'invites.status',
            before:
              previousStatus,
            after:
              'accepted',
            op: 'update',
          },
          {
            memberId: inviteId,
            memberName:
              proposal.affectedNames[0] ??
              'Invite',
            field:
              'invites.accepted_at',
            before:
              previousAcceptedAt,
            after:
              acceptedAt,
            op: 'update',
          },
        ],
        afterValues:
          proposal.fieldsThatWillChange,
        outcome: 'success',
        undoable: true,
      });

      return {
        success: true,
        logId,
        message:
          'Invite status synced to accepted.',
      };
    } catch (error: any) {
      return {
        success: false,
        logId,
        message:
          'Invite repair failed.',
        error:
          error?.message ??
          'Unknown invite repair error.',
      };
    }
  }

  /*
   * Every family_graph_members repair remains review-only until one
   * transactional server authority owns authorization, validation,
   * mutation, rollback, and durable evidence.
   */
  const evidence = {
    generatedAt:
      new Date().toISOString(),
    source:
      'repairActions',
    mode:
      'review-required',
    ownerUserId,
    proposal,
    mutationExecuted: false,
    undoExecuted: false,
    requiredAuthority:
      'server-transactional-governed-tree-repair',
  };

  if (
    typeof window !== 'undefined'
  ) {
    (
      window as unknown as
        Record<string, unknown>
    ).__kcTreeRepairReview =
      evidence;

    window.dispatchEvent(
      new CustomEvent(
        'kc:tree-repair-review',
        {
          detail: evidence,
        },
      ),
    );
  }

  return {
    success: false,
    logId,
    message:
      'Repair review prepared. No Tree data was changed.',
    error:
      'review-required',
  };
};

// ─── Undo ────────────────────────────────────────────────────────────

export interface UndoLastRepairResult {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Undo the most recent successful, undoable repair. Walks the localStorage
 * log from newest → oldest, finds the first entry with `undoable: true`,
 * and inverts its single-field UPDATE. Delete repairs are NEVER eligible
 * for undo (the row is gone). Returns a no-op result with a clear message
 * when no eligible entry exists.
 *
 * NEVER mutates anything outside the exact field the original repair
 * touched. Re-checks live state before writing — if someone else has
 * since modified the field to a different value, the undo is refused.
 */
export const undoLastRepair = async (
  ownerUserId: string | null,
  commandCenterOperationalAuthority: boolean,
): Promise<UndoLastRepairResult> => {
  if (
    !canRunIntelligenceDiagnostics(
      commandCenterOperationalAuthority,
    )
  ) {
    return {
      success: false,
      message:
        'Command Center operational authority is required to undo repairs.',
      error: 'forbidden',
    };
  }

  if (!ownerUserId) {
    return {
      success: false,
      message:
        'Sign in required to undo.',
      error: 'no-session',
    };
  }

  const log = readRepairLog();

  for (
    let index = log.length - 1;
    index >= 0;
    index -= 1
  ) {
    const entry = log[index];

    if (
      entry.ownerUserId !== ownerUserId ||
      entry.outcome !== 'success' ||
      !entry.undoable ||
      entry.actionId !==
        'sync-invite-status'
    ) {
      continue;
    }

    const inviteId =
      entry.afterValues[0]?.memberId ??
      entry.beforeValues[0]?.memberId;

    if (!inviteId) {
      continue;
    }

    const previousStatus =
      (
        entry.beforeValues.find(
          field =>
            field.field ===
            'invites.status',
        )?.before as
          | string
          | null
          | undefined
      ) ?? 'pending';

    const previousAcceptedAt =
      (
        entry.beforeValues.find(
          field =>
            field.field ===
            'invites.accepted_at',
        )?.before as
          | string
          | null
          | undefined
      ) ?? null;

    try {
      const {
        data: liveInvite,
        error: liveReadError,
      } = await supabase
        .from('invites')
        .select('target_member_id')
        .eq('id', inviteId)
        .maybeSingle();

      if (liveReadError) {
        throw liveReadError;
      }

      if (liveInvite?.target_member_id) {
        return {
          success: false,
          message:
            'Person-targeted invite lifecycle is server-owned and cannot be undone through browser repair.',
          error:
            'targeted-invite-server-owned',
        };
      }

      const { error } =
        await supabase
          .from('invites')
          .update({
            status:
              previousStatus,
            accepted_at:
              previousAcceptedAt,
          })
          .eq('id', inviteId);

      if (error) {
        throw error;
      }

      updateRepairLog(
        entry.id,
        {
          undoable: false,
        },
      );

      return {
        success: true,
        message:
          `Undone: ${entry.issueTitle}.`,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          'Undo failed.',
        error:
          error?.message ??
          'Unknown invite undo error.',
      };
    }
  }

  return {
    success: false,
    message:
      'No eligible non-Tree repair to undo.',
    error:
      'no-undoable-entry',
  };
};

/**
 * Returns true when there is at least one undoable, successful repair in
 * the log authored by the given owner. Cheap — used by the
 * TreeIntelligenceCard to decide whether to render the Undo button.
 */
export const hasUndoableRepair = (
  ownerUserId: string | null,
): boolean => {
  if (!ownerUserId) {
    return false;
  }

  const log = readRepairLog();

  return log.some(
    entry =>
      entry.ownerUserId ===
        ownerUserId &&
      entry.outcome ===
        'success' &&
      entry.undoable &&
      entry.actionId ===
        'sync-invite-status',
  );
};
	
